import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IoArrowBack, IoArrowForward, IoCut, IoCamera, IoCalendar, IoLocation, IoStar, IoTime, IoFolder, IoPricetag, IoMegaphone, IoCall, IoCheckmarkCircle } from 'react-icons/io5';
import '../css/AppleDesign.css';
import '../css/ProfessionalDetailPage.css';
import { useToast } from '../common/ToastContext';

const DAYS_LABELS = { lun: 'Lun', mar: 'Mar', mer: 'Mer', jeu: 'Jeu', ven: 'Ven', sam: 'Sam', dim: 'Dim' };

const formatOpeningHours = (str) => {
    try {
        const data = JSON.parse(str);
        if (data && typeof data === 'object' && 'lun' in data) {
            const open = Object.entries(DAYS_LABELS)
                .filter(([key]) => data[key]?.open)
                .map(([key, label]) => {
                    const d = data[key];
                    const base = `${label} ${d.from}–${d.to}`;
                    return d.break ? `${base} (pause ${d.breakFrom || '12:00'}–${d.breakTo || '14:00'})` : base;
                });
            return open.length ? open.join(' · ') : 'Fermé';
        }
    } catch {}
    return str;
};

const formatDisplayedPrice = (price) => `${price}€`;

function ProfessionalDetailPage() {
    const { id, slug } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const toast = useToast();
    const [professional, setProfessional] = useState(null);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [slots, setSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [announcements, setAnnouncements] = useState([]);
    const [serviceSearch, setServiceSearch] = useState('');
    const resolvedProfessionalId = professional?._id || id || null;

    const [bookingData, setBookingData] = useState({
        serviceIds: [],
        date: '',
        hour: '', // Will be 11:00 format
    });

    const toggleServiceSelection = (serviceId) => {
        setBookingData((prev) => {
            const exists = prev.serviceIds.includes(serviceId);
            return {
                ...prev,
                serviceIds: exists
                    ? prev.serviceIds.filter((id) => id !== serviceId)
                    : [...prev.serviceIds, serviceId],
            };
        });
    };

    useEffect(() => {
        loadProfessionalData();
    }, [id, slug]);

    useEffect(() => {
        if (bookingData.date && resolvedProfessionalId) {
            loadSlots(bookingData.date);
        }
    }, [bookingData.date, resolvedProfessionalId]);

    const loadProfessionalData = async () => {
        setLoading(true);
        try {
            let proData = null;

            if (slug) {
                const slugResponse = await fetch(`${window.API_URL}/records/professional/slug/${encodeURIComponent(slug)}`);
                if (slugResponse.ok) {
                    const slugPayload = await slugResponse.json();
                    proData = slugPayload.professional;

                    if (slugPayload.redirectTo && slugPayload.redirectTo !== slug) {
                        navigate(`/pro/${slugPayload.redirectTo}`, { replace: true });
                    }
                }
            }

            if (!proData && id) {
                const proResponse = await fetch(`${window.API_URL}/records/professional/${id}`);
                if (proResponse.ok) {
                    proData = await proResponse.json();
                }
            }

            if (!proData) {
                setProfessional(null);
                setServices([]);
                setAnnouncements([]);
                return;
            }

            setProfessional(proData);
            const professionalId = proData._id;

            const servicesResponse = await fetch(`${window.API_URL}/services/professional/${professionalId}`);
            if (servicesResponse.ok) {
                const servicesData = await servicesResponse.json();
                setServices(servicesData.filter(s => s.isActive));
            }

            const annResponse = await fetch(`${window.API_URL}/announcements/professional/${professionalId}`);
            if (annResponse.ok) {
                const annData = await annResponse.json();
                setAnnouncements(annData);
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadSlots = async (date) => {
        if (!resolvedProfessionalId) {
            setSlots([]);
            return;
        }

        setLoadingSlots(true);
        try {
            const response = await fetch(`${window.API_URL}/availability/slots/${resolvedProfessionalId}?date=${date}`);
            if (response.ok) {
                const data = await response.json();
                setSlots(data);
            } else {
                setSlots([]);
            }
        } catch (error) {
            console.error('Error loading slots:', error);
            setSlots([]);
        } finally {
            setLoadingSlots(false);
        }
    };

    const handleBooking = async (e) => {
        e.preventDefault();

        if (!localStorage.getItem('token')) {
            toast(t('alert_login_required'), 'warning');
            navigate('/login');
            return;
        }

        const selectedServices = services.filter((service) => bookingData.serviceIds.includes(service._id));
        if (selectedServices.length === 0) {
            toast(t('alert_select_service'), 'warning');
            return;
        }

        if (!resolvedProfessionalId) {
            toast(t('pro_not_found'), 'error');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(window.API_URL + '/bookings/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    professionalId: resolvedProfessionalId,
                    serviceIds: bookingData.serviceIds,
                    date: bookingData.date,
                    time: bookingData.hour,
                    notes: '',
                }),
            });

            if (response.ok) {
                toast(t('alert_booking_success'), 'success');
                navigate('/bookings');
            } else {
                toast(t('alert_booking_error'), 'error');
            }
        } catch (error) {
            console.error('Error booking:', error);
            toast(t('alert_booking_error'), 'error');
        }
    };

    if (loading) return <div className="text-center p-5">{t('loading')}</div>;
    if (!professional) return <div className="text-center p-5">{t('pro_not_found')}</div>;

    const selectedServices = services.filter((service) => bookingData.serviceIds.includes(service._id));
    const selectedServicesTotal = selectedServices.reduce((sum, service) => sum + (Number(service.price) || 0), 0);
    // Promos actives (annonces de type promotion avec remise) appliquées en aperçu — le backend reste l'autorité au moment de la réservation
    const promoNow = new Date();
    const activePromos = announcements.filter(
        (a) => (a.type === 'promotion' || a.discountPercent) && Number(a.discountPercent) > 0 && a.isActive !== false
            && (!a.startDate || new Date(a.startDate) <= promoNow)
            && (!a.endDate || new Date(a.endDate) >= promoNow)
    );
    const bestPromoFor = (service) => {
        const applicable = activePromos.filter((p) => !p.serviceId || String(p.serviceId) === String(service._id));
        if (!applicable.length) return null;
        return applicable.reduce((best, p) => (Number(p.discountPercent) > (best ? Number(best.discountPercent) : 0) ? p : best), null);
    };
    const selectedServicesDiscounted = Math.round(selectedServices.reduce((sum, service) => {
        const promo = bestPromoFor(service);
        const price = Number(service.price) || 0;
        return sum + (promo ? price * (1 - Number(promo.discountPercent) / 100) : price);
    }, 0) * 100) / 100;
    const hasActivePromo = selectedServicesDiscounted < selectedServicesTotal - 0.001;
    const galleryPath = professional?.slug
        ? `/pro/${professional.slug}/galerie`
        : `/professional/${professional?._id || id}/galerie`;
    const galleryPreviewPhotos = (professional.salonPhotos || []).slice(0, 6);
    const remainingGalleryPhotos = Math.max((professional.salonPhotos || []).length - galleryPreviewPhotos.length, 0);
    const coverPhoto = professional.coverPhoto || professional.salonPhotos?.[0];
    const filteredServices = services.filter((service) => {
        if (!serviceSearch.trim()) return true;
        const q = serviceSearch.toLowerCase();
        return [service.name, service.category, service.description]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(q));
    });

    return (
        <div className="professional-page" style={{ background: '#F5F5F7', minHeight: '100vh', paddingBottom: '40px' }}>
            {/* Banner */}
            <div style={{
                height: 'clamp(160px, 30vw, 300px)',
                background: coverPhoto
                    ? `url(${window.BASE_URL}${coverPhoto}) center/cover`
                    : 'linear-gradient(135deg, #1d1d1f 0%, #434344 100%)',
                position: 'relative'
            }}>
                <button
                    className="btn btn-secondary"
                    onClick={() => navigate('/recherche')}
                    style={{ position: 'absolute', top: '20px', left: '20px', borderRadius: '50px', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    <IoArrowBack /> {t('return_btn')}
                </button>
            </div>

            <div className="container" style={{ marginTop: '-60px', position: 'relative', zIndex: 10 }}>

                {/* ── Header card ── */}
                <div className="card" style={{ marginBottom: '30px', padding: 0 }}>

                    {/* ── Identity row ── */}
                    <div style={{ padding: '0 clamp(14px, 4vw, 28px) 24px', display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                        <div style={{ marginTop: '-50px', flexShrink: 0 }}>
                            <div style={{
                                width: '100px', height: '100px', borderRadius: '50%',
                                background: `url(${window.BASE_URL}${professional.profilePhoto || ''}) center/cover, #e5e5e5`,
                                border: '4px solid white', boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                            }} />
                        </div>
                        <div style={{ flex: 1, minWidth: '140px', paddingTop: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
                                <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', lineHeight: 1.2 }}>
                                    {professional.companyName || `${professional.prenom} ${professional.nom}`}
                                </h1>
                                <span className="badge badge-primary" style={{ fontSize: '12px', padding: '4px 11px' }}>
                                    {professional.profession || 'Professionnel'}
                                </span>
                            </div>
                            {professional.address && (
                                <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(professional.address)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={t('open_in_maps') || 'Ouvrir dans Maps'}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#86868b', fontSize: '13px', marginBottom: '8px', textDecoration: 'none' }}
                                >
                                    <IoLocation size={14} /> <span style={{ textDecoration: 'underline' }}>{professional.address}</span>
                                </a>
                            )}
                            {professional.description && (
                                <p style={{ margin: 0, color: '#555', fontSize: '14px', lineHeight: '1.6', maxWidth: '540px' }}>
                                    {professional.description}
                                </p>
                            )}
                        </div>
                        <div style={{ paddingTop: '12px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
                            {professional.averageRating > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#FFF9E6', color: '#92600A', padding: '7px 13px', borderRadius: '10px', fontWeight: '700', fontSize: '15px' }}>
                                    <IoStar color="#F5A623" size={16} />
                                    {professional.averageRating.toFixed(1)}
                                    <span style={{ fontWeight: '400', fontSize: '12px', color: '#B8860B' }}>/ 5 ({professional.totalReviews})</span>
                                </div>
                            )}
                            {professional.totalReviews > 0 && (
                                <button className="btn btn-outline btn-sm" onClick={() => navigate(`/reviews/${professional._id || id}`)} style={{ whiteSpace: 'nowrap' }}>
                                    {t('view_reviews')}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* ── Phone + Hours row ── */}
                    <div style={{ borderTop: '1px solid #F0F0F0', display: 'flex', flexWrap: 'wrap' }}>

                        {/* Phone block */}
                        <div style={{ padding: '20px 28px', borderRight: '1px solid #F0F0F0', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '6px', minWidth: '200px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#86868b', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                                <IoCall size={13} /> Téléphone
                            </div>
                            {professional.phone ? (
                                <a href={`tel:${professional.phone}`} style={{ fontWeight: '700', fontSize: '18px', color: 'var(--primary)', textDecoration: 'none', letterSpacing: '0.3px' }}>
                                    {professional.phone}
                                </a>
                            ) : (
                                <span style={{ fontSize: '14px', color: '#bbb', fontStyle: 'italic' }}>Non renseigné</span>
                            )}
                        </div>

                        {/* Hours block */}
                        <div style={{ padding: '20px 28px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#86868b', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '14px' }}>
                                <IoTime size={13} /> Horaires d'ouverture
                            </div>
                            {professional.openingHours ? (() => {
                                try {
                                    const data = JSON.parse(professional.openingHours);
                                    if (data && 'lun' in data) {
                                        const DAYS = [
                                            { key: 'lun', short: 'Lun' }, { key: 'mar', short: 'Mar' },
                                            { key: 'mer', short: 'Mer' }, { key: 'jeu', short: 'Jeu' },
                                            { key: 'ven', short: 'Ven' }, { key: 'sam', short: 'Sam' },
                                            { key: 'dim', short: 'Dim' },
                                        ];
                                        return (
                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                {DAYS.map(({ key, short }) => {
                                                    const d = data[key];
                                                    const isOpen = d?.open;
                                                    return (
                                                        <div key={key} style={{
                                                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                                                            padding: '8px', borderRadius: '12px', minWidth: '44px', flex: '1 1 44px',
                                                            background: isOpen ? '#F0FFF4' : '#F8F8F8',
                                                            border: `1px solid ${isOpen ? '#C6F6D5' : '#EBEBEB'}`,
                                                            gap: '4px'
                                                        }}>
                                                            <span style={{ fontWeight: '700', fontSize: '12px', color: isOpen ? '#1B6B3A' : '#bbb', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{short}</span>
                                                            {isOpen ? (
                                                                <>
                                                                    <span style={{ fontWeight: '600', fontSize: '12px', color: '#1d1d1f', whiteSpace: 'nowrap' }}>{d.from} – {d.to}</span>
                                                                    {d.break && (
                                                                        <span style={{ fontSize: '10px', color: '#86868b', whiteSpace: 'nowrap', marginTop: '2px' }}>
                                                                            pause {d.breakFrom}–{d.breakTo}
                                                                        </span>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <span style={{ fontSize: '11px', color: '#bbb', fontStyle: 'italic' }}>Fermé</span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    }
                                } catch {}
                                return <span style={{ fontSize: '14px', color: '#555' }}>{professional.openingHours}</span>;
                            })() : (
                                <span style={{ fontSize: '14px', color: '#bbb', fontStyle: 'italic' }}>Non renseignés</span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="grid grid-2" style={{ alignItems: 'start' }}>
                    {/* Services and Gallery */}
                    <div className="left-column">
                        {announcements.length > 0 && (
                            <div className="card" style={{ marginBottom: '30px', borderLeft: '5px solid var(--primary)', background: 'linear-gradient(to right, #ffffff, #f9fbfc)' }}>
                                <h2 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}><IoMegaphone color="var(--primary)" /> {t('announcements_title') || 'Annonces & Promotions'}</h2>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    {announcements.map(ann => (
                                        <div key={ann._id} style={{ padding: '10px 0', borderBottom: announcements.length > 1 ? '1px solid #f5f5f7' : 'none' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                                                <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{ann.title}</h3>
                                                {ann.discountPercent && <span className="badge badge-primary">-{ann.discountPercent}%</span>}
                                            </div>
                                            <p style={{ margin: 0, fontSize: '0.95rem', color: 'var(--text-secondary)' }}>{ann.description}</p>
                                            {ann.serviceId && (
                                                <div style={{ marginTop: '5px', fontSize: '0.85rem', color: 'var(--primary)', fontWeight: '500' }}>
                                                    Valable sur : {services.find(s => s._id === ann.serviceId)?.name || 'Cette prestation'}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="card" style={{ marginBottom: '30px' }}>
                            <h2 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}><IoCut /> {t('services_title')}</h2>
                            {services.length > 0 ? (
                                <div className="services-list">
                                    {services.map(service => (
                                        <div
                                            key={service._id}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                padding: '15px 0',
                                                borderBottom: '1px solid #f5f5f7',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => toggleServiceSelection(service._id)}
                                        >
                                            <div>
                                                <div style={{ fontWeight: '600', marginBottom: '4px', color: bookingData.serviceIds.includes(service._id) ? 'var(--primary)' : 'inherit' }}>
                                                    {service.name}
                                                </div>
                                                <div className="text-secondary" style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><IoTime size={14} /> {service.duration} min</span>
                                                    <span>•</span>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><IoFolder size={14} /> {service.category}</span>
                                                </div>
                                                {service.description && <div className="text-secondary" style={{ fontSize: '12px', marginTop: '4px' }}>{service.description}</div>}
                                            </div>
                                            <div style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <IoPricetag size={14} /> {formatDisplayedPrice(service.price)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-secondary">{t('no_services')}</p>
                            )}
                        </div>

                        {professional.salonPhotos?.length > 0 && (
                            <div className="card professional-gallery-preview-card">
                                <div className="professional-gallery-preview-head">
                                    <h2><IoCamera /> {t('gallery_title')}</h2>
                                    <button
                                        type="button"
                                        className="btn btn-outline btn-sm"
                                        onClick={() => navigate(galleryPath)}
                                    >
                                        Voir toute la galerie <IoArrowForward />
                                    </button>
                                </div>
                                <div className="professional-gallery-preview-grid">
                                    {galleryPreviewPhotos.map((photo, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            className="professional-gallery-preview-item"
                                            onClick={() => navigate(`${galleryPath}?photo=${i}`)}
                                        >
                                            <img src={`${window.BASE_URL}${photo}`} alt={`Galerie ${i + 1}`} />
                                            {remainingGalleryPhotos > 0 && i === galleryPreviewPhotos.length - 1 && (
                                                <span>+{remainingGalleryPhotos}</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Booking Form */}
                    <div className="booking-card card" style={{ position: 'sticky', top: '20px' }}>
                        <h2 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}><IoCalendar /> {t('booking_title')}</h2>
                        <form onSubmit={handleBooking}>
                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label className="form-label">{t('label_select_service')}</label>
                                {services.length > 6 && (
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder={t('search_placeholder', { defaultValue: 'Rechercher une prestation...' })}
                                        value={serviceSearch}
                                        onChange={(e) => setServiceSearch(e.target.value)}
                                        style={{ marginBottom: '10px' }}
                                    />
                                )}
                                <div className="booking-services-picker">
                                    {filteredServices.map((service) => {
                                        const isSelected = bookingData.serviceIds.includes(service._id);
                                        return (
                                            <button
                                                key={service._id}
                                                type="button"
                                                className={`booking-service-option${isSelected ? ' is-selected' : ''}`}
                                                onClick={() => toggleServiceSelection(service._id)}
                                                aria-pressed={isSelected}
                                                style={{
                                                    border: 'none',
                                                }}
                                            >
                                                <span className="booking-service-option__main">
                                                    <span className="booking-service-option__title-row">
                                                        <span className="booking-service-option__title">{service.name}</span>
                                                        {isSelected && (
                                                            <span className="booking-service-option__selected">
                                                                <IoCheckmarkCircle size={14} />
                                                                {t('selected', { defaultValue: 'Sélectionnée' })}
                                                            </span>
                                                        )}
                                                    </span>
                                                    <span className="booking-service-option__meta">
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><IoTime size={13} /> {service.duration} min</span>
                                                        {service.category && (
                                                            <>
                                                                <span>•</span>
                                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><IoFolder size={13} /> {service.category}</span>
                                                            </>
                                                        )}
                                                    </span>
                                                </span>
                                                <strong className="booking-service-option__price">{formatDisplayedPrice(service.price)}</strong>
                                            </button>
                                        );
                                    })}
                                </div>
                                {filteredServices.length === 0 && (
                                    <div className="text-secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
                                        {t('no_services')}
                                    </div>
                                )}
                                {bookingData.serviceIds.length === 0 && (
                                    <div className="text-secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
                                        {t('placeholder_select_service')}
                                    </div>
                                )}
                                {selectedServices.length > 0 && (
                                    <div className="booking-selected-services-summary">
                                        <div className="booking-selected-services-summary__count">
                                            {t('booking_services_selected_count', {
                                                count: selectedServices.length,
                                                defaultValue: `${selectedServices.length} prestation(s) sélectionnée(s)`,
                                            })}
                                        </div>
                                        <div className="booking-selected-services-summary__chips">
                                            {selectedServices.map((service) => (
                                                <span key={service._id} className="booking-selected-services-summary__chip">
                                                    {service.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label className="form-label">{t('label_select_date')}</label>
                                <input
                                    type="date"
                                    className="form-input"
                                    required
                                    min={new Date().toISOString().split('T')[0]}
                                    value={bookingData.date}
                                    onChange={(e) => setBookingData({ ...bookingData, date: e.target.value, hour: '' })}
                                />
                            </div>

                            {bookingData.date && (
                                <div className="form-group" style={{ marginBottom: '20px' }}>
                                    <label className="form-label">{t('label_select_slot')}</label>
                                    {loadingSlots ? (
                                        <div className="text-secondary">{t('loading_slots')}</div>
                                    ) : slots.length > 0 ? (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                                            {slots.map((slot, index) => (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    disabled={!slot.available}
                                                    className={`btn ${bookingData.hour === slot.time ? 'btn-primary' : 'btn-outline'}`}
                                                    style={{
                                                        padding: '8px',
                                                        fontSize: '13px',
                                                        opacity: slot.available ? 1 : 0.5,
                                                        textDecoration: slot.available ? 'none' : 'line-through'
                                                    }}
                                                    onClick={() => slot.available && setBookingData({ ...bookingData, hour: slot.time })}
                                                >
                                                    {slot.time}
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-secondary">{t('no_slots_available')}</div>
                                    )}
                                </div>
                            )}

                            {selectedServices.length > 0 && bookingData.hour && (
                                <div style={{ padding: '15px', background: '#F5F5F7', borderRadius: '12px', marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                        <span>{t('total_label')}</span>
                                        <strong>
                                            {hasActivePromo ? (
                                                <>
                                                    <span style={{ textDecoration: 'line-through', color: '#86868b', marginRight: '6px', fontWeight: 400 }}>
                                                        {formatDisplayedPrice(selectedServicesTotal)}
                                                    </span>
                                                    <span style={{ color: 'var(--primary)' }}>{formatDisplayedPrice(selectedServicesDiscounted)}</span>
                                                </>
                                            ) : (
                                                formatDisplayedPrice(selectedServicesTotal)
                                            )}
                                        </strong>
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                                        {selectedServices.map((service) => service.name).join(', ')}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#86868b' }}>
                                        {new Date(bookingData.date).toLocaleDateString(t('locale') === 'en' ? 'en-US' : 'fr-FR')} à {bookingData.hour}
                                    </div>
                                </div>
                            )}

                            <button
                                type="submit"
                                className="btn btn-primary btn-lg"
                                style={{ width: '100%' }}
                                disabled={bookingData.serviceIds.length === 0 || !bookingData.date || !bookingData.hour}
                            >
                                {t('confirm_booking_btn')}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProfessionalDetailPage;
